<template>
  <div>
    <v-fab-transition v-if="isModification">
      <v-btn v-hotkey="'ctrl+s'" color="green" @click="save" icon size="large" class="fab-fixed-bottom-right">
        <v-icon size="large">mdi-content-save-outline</v-icon>
      </v-btn>
    </v-fab-transition>
    <!-- Stacked number inputs are taller than compact fields: give every field here the same (comfortable) height -->
    <v-defaults-provider :defaults="fieldDefaults">
      <v-form ref="sectionGeneral" v-model="valid">
        <v-container fluid class="px-0">
          <v-alert v-if="errorMessage" density="compact" variant="tonal" border="start" type="error" class="mb-3">
            {{ errorMessage }}
          </v-alert>
          <v-alert v-if="isModification" density="compact" variant="tonal" border="start" type="warning" class="mb-3">
            Modifications non sauvegardées. Sauvegardez (CTRL+S) avant de changer de section ou d'onglet.
          </v-alert>

          <div class="d-flex align-center flex-wrap ga-6 mb-2">
            <v-switch v-model="section.published" label="Publiée"></v-switch>
            <v-switch v-model="section.free_access" label="Gratuite"></v-switch>
            <v-spacer></v-spacer>
            <v-btn
              v-if="!isDeleteSection"
              variant="text"
              color="error"
              size="small"
              prepend-icon="mdi-trash-can-outline"
              @click="deleteSectionConfirmation()"
            >
              Supprimer
            </v-btn>
            <div v-else class="d-flex align-center ga-2">
              <span class="text-body-small text-error">Supprimer définitivement cette section ?</span>
              <v-btn variant="flat" color="error" size="small" @click="deleteSection()">Supprimer</v-btn>
              <v-btn variant="text" size="small" @click="deleteSectionCancel()">Annuler</v-btn>
            </div>
          </div>

          <h3 class="form-section-title">Identification</h3>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-combobox
                v-model="section.region"
                :items="regions"
                item-title="name"
                item-value="name"
                label="Région"
                :return-object="false"
                required
              ></v-combobox>
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                v-model="section.river"
                :items="rivers"
                item-title="name"
                item-value="name"
                label="Nom de la rivière"
                :return-object="false"
                required
              ></v-combobox>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="Nom de la section" v-model="section.name"></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="section.type"
                :items="sectionTypes"
                label="Catégorie de rivière"
                required
              ></v-autocomplete>
            </v-col>
          </v-row>

          <h3 class="form-section-title">Caractéristiques</h3>
          <v-row dense>
            <v-col cols="6" md>
              <v-text-field label="Longueur (km)" v-model="section.length"></v-text-field>
            </v-col>
            <v-col cols="6" md>
              <v-number-input
                label="Nombre de rapides"
                :model-value="toNumberOrNull(section.rapidsAmount)"
                :min="0"
                control-variant="stacked"
                @update:model-value="section.rapidsAmount = $event"
              ></v-number-input>
            </v-col>
            <v-col cols="6" md>
              <v-text-field label="Portages" v-model="section.portage"></v-text-field>
            </v-col>
            <v-col cols="6" md>
              <v-number-input
                label="Gradient"
                :model-value="toNumberOrNull(section.gradient)"
                :min="0"
                :precision="null"
                control-variant="stacked"
                @update:model-value="section.gradient = $event"
              ></v-number-input>
            </v-col>
            <v-col cols="6" md>
              <v-number-input
                label="Navette (km)"
                :model-value="toNumberOrNull(section.shuttle)"
                :min="0"
                :precision="null"
                control-variant="stacked"
                @update:model-value="section.shuttle = $event"
              ></v-number-input>
            </v-col>
          </v-row>

          <div class="d-flex align-center justify-space-between">
            <h3 class="form-section-title">Paliers de débit</h3>
            <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-plus" @click="addFlowLevel()">
              Ajouter un palier
            </v-btn>
          </div>
          <p class="text-body-small text-medium-emphasis mb-3">
            Cochez les paliers recommandés : le plus bas et le plus haut forment la plage recommandée et son milieu est le
            débit idéal. L'échelle (ex. -1, 0, +2) relie un débit à un repère visuel sur la rivière et s'affiche sur le
            graphique.
          </p>
          <v-row
            v-for="(level, i) in levels"
            :key="i"
            dense
            align="center"
            :class="['flow-level-row', `flow-level-row--${getZone(level) || 'none'}`]"
          >
            <v-col cols="6" md="2">
              <v-text-field
                v-model="level.gauge_mark"
                label="Échelle"
                placeholder="+1"
                @update:model-value="handleChange"
              ></v-text-field>
            </v-col>
            <v-col cols="6" md="3">
              <v-number-input
                label="Débit (mcs)"
                :model-value="toNumberOrNull(level.flow)"
                :min="0"
                :precision="null"
                control-variant="stacked"
                @update:model-value="
                  level.flow = $event;
                  handleChange();
                "
              ></v-number-input>
            </v-col>
            <v-col cols="6" md="3">
              <v-autocomplete
                v-model="level.cotation"
                :items="cotations"
                label="Cotation"
                @update:model-value="handleChange"
              ></v-autocomplete>
            </v-col>
            <v-col cols="6" md="4" class="d-flex align-center ga-2">
              <v-checkbox
                v-model="level.recommended"
                label="Recommandé"
                color="success"
                density="compact"
                hide-details
                @update:model-value="handleChange"
              ></v-checkbox>
              <v-chip v-if="getZone(level)" size="small" label variant="tonal" :color="zones[getZone(level)].color">
                {{ zones[getZone(level)].label }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn
                icon="mdi-delete-outline"
                variant="text"
                size="small"
                color="error"
                title="Retirer ce palier"
                @click="removeFlowLevel(i)"
              ></v-btn>
            </v-col>
          </v-row>
          <v-alert
            v-if="derivedLevels.min_sug_level === null"
            type="info"
            variant="tonal"
            density="compact"
            class="mt-2"
          >
            Cochez au moins un palier recommandé (avec un débit) pour définir la plage recommandée.
          </v-alert>
          <div v-else class="text-body-small mt-2">
            Plage recommandée :
            <strong>{{ derivedLevels.min_sug_level }} à {{ derivedLevels.max_sug_level }} mcs</strong>
            · idéal <strong>{{ derivedLevels.recommended_sug_level }} mcs</strong> · cotations
            <strong>
              {{ derivedLevels.class_level_min || "—" }} / {{ derivedLevels.class_level || "—" }} /
              {{ derivedLevels.class_level_max || "—" }}
            </strong>
          </div>

          <h3 class="form-section-title">Description</h3>
          <rich-text-editor v-model="section.description"></rich-text-editor>
        </v-container>
      </v-form>
    </v-defaults-provider>
  </div>
</template>

<script>
  import RichTextEditor from "@/components/RichTextEditor.vue";
  import { RiversService } from "@/services/Rivers.service";
  import SectionsService from "@/store/modules/sections";
  import cotations from "@/store/db/cotations.db";
  import sectionTypes from "@/store/db/sectionTypes.db";
  import { notify } from "@/services/Notification.service";
  import { toNumberOrNull } from "@/utils/numbers";
  import {
    createFlowLevel,
    deriveSectionLevels,
    getFlowLevelZone,
    getSectionFlowLevels,
    sortFlowLevels,
    sortFlowLevelsHighestFirst,
  } from "@/utils/flowLevels";

  const zones = {
    min: { label: "Zone min", color: "error" },
    recommended: { label: "Recommandé", color: "success" },
    max: { label: "Zone max", color: "info" },
  };
  export default {
    components: { RichTextEditor },
    props: {
      section: {
        type: Object,
        default: function () {
          return {};
        },
      },
      reloadData: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isDeleteSection: false,
        isModification: false,
        errorMessage: "",
        valid: true,
        cotations: cotations,
        // Edited here and written to section.flow_levels (plus the derived legacy fields) on save
        levels: [],
        zones,
        sectionTypes: sectionTypes,
        textFieldRules: [
          (value) => !!value || "Required.",
          (value) => (value && value.length >= 3) || "Min 3 characters",
        ],
      };
    },
    async created() {
      await this.getRegions();
      await this.getRivers();
      this.ensureClassLevelsCompatibility();
      this.ensurePortageCompatibility();
      this.loadFlowLevels();
    },
    methods: {
      toNumberOrNull,
      loadFlowLevels() {
        const levels = getSectionFlowLevels(this.section);
        this.levels = levels.length
          ? sortFlowLevelsHighestFirst(levels)
          : [createFlowLevel(), createFlowLevel(), createFlowLevel()];
      },
      addFlowLevel() {
        this.levels.push(createFlowLevel({ recommended: false }));
        this.handleChange();
      },
      removeFlowLevel(index) {
        this.levels.splice(index, 1);
        this.handleChange();
      },
      getZone(level) {
        return getFlowLevelZone(level, this.derivedLevels);
      },
      // Writes the rows (lowest flow first) and the legacy fields derived from them; false if a row has no flow.
      // The editor then lists them highest flow first, like the level chart.
      applyFlowLevels() {
        const isFilled = (level) => toNumberOrNull(level.flow) !== null || level.cotation || level.gauge_mark;
        if (this.levels.some((level) => isFilled(level) && toNumberOrNull(level.flow) === null)) {
          this.errorMessage = "Chaque palier de débit doit avoir un débit.";
          return false;
        }
        const flowLevels = sortFlowLevels(this.levels).map((level) => ({
          flow: level.flow,
          cotation: level.cotation || "",
          recommended: Boolean(level.recommended),
          gauge_mark: String(level.gauge_mark || "").trim(),
        }));
        Object.assign(this.section, { flow_levels: flowLevels, ...deriveSectionLevels(flowLevels) });
        this.levels = sortFlowLevelsHighestFirst(flowLevels.map((level) => createFlowLevel(level)));
        return true;
      },
      ensureClassLevelsCompatibility() {
        if (!this.section || typeof this.section !== "object") {
          return;
        }

        const hasClassLevel = Object.prototype.hasOwnProperty.call(this.section, "class_level");
        const hasClassLevelMin = Object.prototype.hasOwnProperty.call(this.section, "class_level_min");

        if (!hasClassLevelMin && hasClassLevel) {
          this.section.class_level_min = this.section.class_level;
        }

        if (!hasClassLevel && hasClassLevelMin) {
          this.section.class_level = this.section.class_level_min;
        }

        if (hasClassLevelMin && (!this.section.class_level || this.section.class_level === "")) {
          this.section.class_level = this.section.class_level_min;
        }
      },
      ensurePortageCompatibility() {
        if (!this.section || typeof this.section !== "object") {
          return;
        }

        const hasPortage = Object.prototype.hasOwnProperty.call(this.section, "portage");
        const hasPortageMin = Object.prototype.hasOwnProperty.call(this.section, "portage_min");
        const hasPortageMax = Object.prototype.hasOwnProperty.call(this.section, "portage_max");

        if (!hasPortage && hasPortageMin) {
          this.section.portage = this.section.portage_min;
          return;
        }

        if (!hasPortage && hasPortageMax) {
          this.section.portage = this.section.portage_max;
        }
      },
      async getRegions() {
        await this.$store.dispatch("bindRegions");
      },
      async getRivers() {
        await this.$store.dispatch("bindRivers");
      },
      getTotalRapids(obj) {
        return RiversService.getTotalRapids(obj);
      },
      handleChange() {
        this.errorMessage = "";
        this.isModification = true;
      },
      async save() {
        if (this.valid) {
          const riverExist = this.lookIfRiverExist();
          console.log("riverExist is " + riverExist); // TODO add river (create alias)
          const regionExist = this.lookIfRegionExist();
          console.log("regionExist is " + regionExist); // TODO add région (create alias)

          this.updateSection();
        } else {
          this.errorMessage = "Certains champs obligatoires ne répondent pas aux critères attendus, veuillez vérifier.";
        }
      },
      updateSection() {
        if (!this.applyFlowLevels()) {
          return;
        }
        // The store computes the alias on a copy; keep the edited section in sync so the other tabs unlock
        this.section.alias = SectionsService.actions.createAlias(this.section.river + " " + this.section.name);
        SectionsService.actions
          .updateSection(this.section)
          .then(() => {
            this.isModification = false;
            notify("La section a été sauvegardée avec succès.");
          })
          .catch((error) => {
            console.log(error);
            this.errorMessage =
              "Une erreur s'est produite lors de la sauvegarde de la section, vérifiez les résultats et recommencez.";
          });
      },
      deleteSectionConfirmation() {
        this.isDeleteSection = true;
      },
      deleteSectionCancel() {
        this.isDeleteSection = false;
      },
      deleteSection() {
        SectionsService.actions
          .deleteSection(this.section)
          .then(() => {
            this.deleteSectionCancel();
            this.$emit("deleted", this.section);
          })
          .catch((error) => {
            console.log(error);
            this.errorMessage =
              "Une erreur s'est produite lors de la suppression de la section, vérifiez les résultats et recommencez.";
          });
      },
      lookIfRiverExist() {
        const rivers = this.$store.getters.rivers;
        var exist = false;
        rivers.forEach((river) => {
          if (river.name.toLowerCase() === this.section.river.toLowerCase()) {
            exist = true;
          }
        });
        return exist;
      },
      lookIfRegionExist() {
        const regions = this.$store.getters.regions;
        var exist = false;
        regions.forEach((region) => {
          if (region.name.toLowerCase() === this.section.region.toLowerCase()) {
            exist = true;
          }
        });
        return exist;
      },
    },
    computed: {
      fieldDefaults() {
        const comfortable = { density: "comfortable" };
        return { VTextField: comfortable, VCombobox: comfortable, VAutocomplete: comfortable, VNumberInput: comfortable };
      },
      derivedLevels() {
        return deriveSectionLevels(this.levels);
      },
      regions() {
        return this.$store.getters.regions;
      },
      rivers() {
        return this.$store.getters.rivers;
      },
    },
    watch: {
      "section.id"() {
        this.loadFlowLevels();
      },
      section: {
        deep: true,
        handler(oldValue, newValue) {
          this.ensureClassLevelsCompatibility();
          this.ensurePortageCompatibility();
          this.errorMessage = "";
          if (oldValue.id !== newValue.id) {
            this.isModification = false;
          } else {
            this.isModification = true;
          }
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .flow-level-row {
    border-left: 3px solid transparent;
    margin-bottom: 4px;
  }
  .flow-level-row--min {
    border-left-color: rgb(var(--v-theme-error));
  }
  .flow-level-row--recommended {
    border-left-color: rgb(var(--v-theme-success));
  }
  .flow-level-row--max {
    border-left-color: rgb(var(--v-theme-info));
  }
  // Let the stacked +/- buttons share the field height instead of forcing it taller
  :deep(.v-number-input--stacked .v-number-input__control .v-btn) {
    flex: 1 1 0;
    height: auto;
    min-height: 0;
  }
  .form-section-title {
    margin: 20px 0 8px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }
</style>
